import { UsersThree } from '@phosphor-icons/react';
import { addDays, includesMember } from '@shh/shared';
import { Avatar, Icon, cn, personSoft } from '@shh/ui';
import { useLayoutEffect, useRef } from 'react';
import { Button as AriaButton } from 'react-aria-components';
import { calendarDay, daySpans, isSpan, type Layers } from '../../data/calendar';
import type { E } from '../../data/derive';
import type { FamilyData } from '../../data/family';
import { calendarText } from '../../i18n/calendar';
import { useSwipePane } from '../../lib/useSwipePane';
import { dayNumber, weekdayShort } from './format';

/**
 * The week by person (Mike, 18.10.: "Woche nur nach Personen"): one row per person plus the
 * family, one column per day – who is where, when. On phones the days scroll sideways under the
 * fixed faces; a slow drag moves them, a quick flick turns the week.
 */
/** One lane of bars for events over several days. */
const BAR = '1.75rem';

export function WeekByPerson({
  f,
  weekOf,
  now,
  layers,
  people,
  onOpen,
  onStep,
}: {
  f: FamilyData;
  weekOf: string;
  now: string;
  layers: Layers;
  people?: string[];
  onOpen: (id: string, date: string) => void;
  onStep: (dir: 1 | -1) => void;
}) {
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekOf, i));
  const lists = days.map((d) => calendarDay(f, d, { layers, people }));
  const members = f.members.filter((m) => !people || people.includes(m.id));
  const { ref: scroller, style } = useSwipePane<HTMLDivElement>(onStep, { scrollX: true });
  const todayCol = useRef<HTMLDivElement>(null);
  const mondayCol = useRef<HTMLDivElement>(null);

  // open at today's column on small screens (the week scrolls sideways there)
  useLayoutEffect(() => {
    if (!window.matchMedia?.('(max-width: 639px)').matches) return;
    // today's column, or Monday when today is in another week
    (todayCol.current ?? mondayCol.current)?.scrollIntoView({ block: 'nearest', inline: 'start' });
  }, [weekOf]);

  const tone = (e: E) =>
    cn(
      e.virtual === 'birthday'
        ? 'bg-accent-tertiary-soft'
        : e.assignees === 'all'
          ? 'bg-accent-secondary-soft'
          : (personSoft[
              f.members.find((m) => includesMember(e.assignees, m.id))?.color ?? 'stone'
            ] ?? ''),
      e.source === 'ics' && 'outline-dashed outline-1 outline-border-default',
    );

  // one bar per event over several days, per person row
  const spansOf = (pick: (e: E) => boolean) => daySpans(days, lists, pick);

  const pill = (e: E, date: string) => (
    <AriaButton
      key={e.id}
      onPress={() => onOpen(e.id, date)}
      className={cn(
        'mb-1 block w-full cursor-pointer rounded-control px-1.5 py-1 text-left text-xs font-bold leading-tight outline-none hyphens-auto',
        'data-focus-visible:outline-3 data-focus-visible:outline-border-focus data-pressed:scale-97',
        tone(e),
      )}
    >
      {e.title}
      {e.start && (
        <span className="block font-semibold text-text-secondary tabular-nums">
          {e.start}
          {e.end ? `–${e.end}` : ''}
        </span>
      )}
    </AriaButton>
  );

  const nowMark = <span aria-hidden className="my-1 block h-0.5 rounded-chip bg-accent-primary" />;
  const cell = (list: E[], date: string) => {
    const isToday = date === f.today;
    const out = list.filter((e) => !isSpan(e)).map((e) => pill(e, date));
    if (isToday) {
      const at = list.findIndex((e) => e.start && e.start > now);
      out.splice(at < 0 ? out.length : at, 0, <span key="now">{nowMark}</span>);
    }
    return out;
  };

  const rows: { key: string; head: React.ReactNode; pick: (e: E) => boolean }[] = [
    ...members.map((m) => ({
      key: m.id,
      head: (
        <>
          <Avatar name={m.name} color={m.color} emoji={m.emoji} size="sm" decorative />
          <span className="max-sm:sr-only">
            {m.id === f.meId ? calendarText.me(m.name) : m.name}
          </span>
        </>
      ),
      pick: (e: E) =>
        e.virtual !== 'holiday' && e.assignees !== 'all' && includesMember(e.assignees, m.id),
    })),
    {
      key: 'family',
      head: (
        <>
          <span className="grid size-avatar-sm place-items-center rounded-avatar bg-accent-secondary text-accent-on-accent">
            <Icon icon={UsersThree} size="sm" weight="fill" />
          </span>
          <span className="max-sm:sr-only">{calendarText.family}</span>
        </>
      ),
      pick: (e: E) => e.virtual !== 'holiday' && e.assignees === 'all',
    },
  ];

  return (
    <div
      ref={scroller}
      style={style}
      className="touch-pan-y overflow-x-auto overscroll-x-contain rounded-card bg-surface-raised [scrollbar-width:none]"
    >
      <div
        role="table"
        aria-label={calendarText.whoWhereWhen}
        className="grid min-w-max grid-cols-[3rem_repeat(7,5.5rem)] sm:min-w-0 sm:grid-cols-[8rem_repeat(7,minmax(0,1fr))]"
      >
        <div role="row" className="contents">
          <div role="columnheader" className="sticky left-0 z-10 bg-surface-raised" />
          {days.map((d, i) => {
            const hol = lists[i]!.find((e) => e.virtual === 'holiday');
            const isToday = d === f.today;
            return (
              <div
                key={d}
                role="columnheader"
                ref={isToday ? todayCol : i === 0 ? mondayCol : undefined}
                className={cn(
                  'relative flex scroll-ml-12 flex-col items-center border-l border-border-subtle px-1 pb-2 pt-1 text-sm font-black',
                  isToday && 'bg-feedback-danger-surface text-feedback-danger',
                )}
              >
                <span>{weekdayShort(d)}</span>
                <span
                  className={cn(
                    'tabular-nums',
                    isToday
                      ? 'rounded-chip bg-feedback-danger px-1.5 text-accent-on-accent'
                      : 'text-text-secondary',
                  )}
                >
                  {dayNumber(d)}.
                </span>
                {hol && (
                  <span
                    title={hol.title}
                    className={cn(
                      'absolute inset-x-0 bottom-0 h-1',
                      hol.publicHoliday ? 'bg-feedback-danger' : 'bg-feedback-warning-surface',
                    )}
                  />
                )}
              </div>
            );
          })}
        </div>
        {rows.map((r) => (
          <div key={r.key} role="row" className="contents">
            <div
              role="rowheader"
              className="sticky left-0 z-10 flex items-center justify-center gap-gap-xs border-t border-border-subtle bg-surface-raised px-1 font-bold sm:justify-start sm:px-inset-xs"
            >
              {r.head}
            </div>
            {(() => {
              const bars = spansOf(r.pick);
              const lanes = bars.reduce((n, b) => Math.max(n, b.lane + 1), 0);
              return days.map((d, i) => (
                <div
                  key={d}
                  role="cell"
                  className={cn(
                    'relative min-h-16 border-l border-t border-border-subtle p-1',
                    d === f.today && 'bg-feedback-danger-surface',
                  )}
                  style={lanes ? { paddingTop: `calc(0.25rem + ${lanes} * ${BAR})` } : undefined}
                >
                  {bars
                    .filter((b) => b.from === i)
                    .map((b) => (
                      <AriaButton
                        key={b.e.id}
                        onPress={() => onOpen(b.e.id, d)}
                        aria-label={`${b.e.title}, ${dayNumber(b.e.date)}.–${dayNumber(b.e.endDate!)}.`}
                        className={cn(
                          'absolute left-1 z-5 h-6 cursor-pointer truncate rounded-control px-1.5 text-left text-xs font-bold leading-6 outline-none',
                          'data-focus-visible:outline-3 data-focus-visible:outline-border-focus',
                          tone(b.e),
                          b.cutStart && 'rounded-l-none',
                          b.cutEnd && 'rounded-r-none',
                        )}
                        // one bar across all its days (Mike, 19.10.: "einmal durchgehend")
                        style={{
                          top: `calc(0.25rem + ${b.lane} * ${BAR})`,
                          width: `calc(${b.len} * 100% + ${b.len - 1}px - 0.5rem)`,
                        }}
                      >
                        {b.e.title}
                      </AriaButton>
                    ))}
                  {cell(lists[i]!.filter(r.pick), d)}
                </div>
              ));
            })()}
          </div>
        ))}
      </div>
    </div>
  );
}
